import React, { useEffect, useState, useContext } from 'react';
import { Formik } from 'formik';

import { Card } from 'src/components';
import {
  SalesforceContext,
  ecommerceProductCategoryName,
  AcquiringChannelFlags,
  paymentRequestProductCategoryName,
} from 'src/context/salesforce-context';
import { FeesContext } from 'src/context/fees-context';
import { PAYMENT_LIST } from 'src/helpers/constants';
import TealiumEvents from 'src/helpers/tealium-events';
import { Button } from 'src/features/common';
import {
  DefaultButtonStyle,
  ButtonParamType,
} from 'src/features/common/components/button/revamp-button';
import {
  PRODUCT_IMAGE_MAP,
  DEVICE_NAMES,
} from 'src/features/order/tech-debt/product-details-extra';
import './payment-types.scss';

export default function PaymentType(props) {
  const {
    uniqueTerminals,
    merchant,
    getDeviceOrderQuantity,
    setDeviceOrder,
    customerType,
    isLoadingFromSessionStorage,
  } = useContext(SalesforceContext);
  const { terminalFees } = useContext(FeesContext);

  const [initialValues, setInitialValues] = useState([]);
  useEffect(() => {
    props.toggleNext(false);
    TealiumEvents.paymentTypesPageLoad(customerType);
  }, []);

  useEffect(() => {
    setInitialValues(getDeviceQuantities());
  }, [merchant]);

  useEffect(() => {
    setInitialValues(setDefaultTenderType(DEVICE_NAMES.VISA_MASTERCARD));
  }, []);

  useEffect(() => props.toggleNext(!isLoadingFromSessionStorage && canGoNext()), [merchant]);

  function quantityFieldNameToDeviceId(fieldname) {
    return fieldname.substring(16);
  }

  function deviceIdToQuantityFieldName(deviceId) {
    return `Quantity_Device_${deviceId}`;
  }

  function getDeviceQuantities() {
    return Object.values(uniqueTerminals)
      .filter(
        (term) =>
          (term.product_category === ecommerceProductCategoryName ||
            term.product_category === paymentRequestProductCategoryName) &&
          !term.is_product_category,
      )
      .reduce((accum, terminal) => {
        const deliveryInfo = merchant?.opportunity?.deliveries?.[terminal.id];
        const numberOfDevices = deliveryInfo ? deliveryInfo.number_of_devices : 0;
        const fieldName = deviceIdToQuantityFieldName(terminal.id);
        accum[fieldName] = numberOfDevices;
        return accum;
      }, {});
  }

  const getSelectedProductCategories = () => {
    return Object.values(merchant?.opportunity?.deliveries)
      .filter((terminal) => terminal.is_product_category && terminal.number_of_devices > 0)
      .map((terminal) => terminal.product_category);
  };

  function canGoNext() {
    const totalDevicesGateway = Object.values(merchant?.opportunity?.deliveries).reduce(
      (currentTotal, product) => {
        return product.number_of_devices > 0 &&
          product.product_category === ecommerceProductCategoryName &&
          !product.is_product_category
          ? currentTotal + product.number_of_devices
          : currentTotal;
      },
      0,
    );

    const totalDevicesRequest = Object.values(merchant?.opportunity?.deliveries).reduce(
      (currentTotal, product) => {
        return product.number_of_devices > 0 &&
          product.product_category === paymentRequestProductCategoryName &&
          !product.is_product_category
          ? currentTotal + product.number_of_devices
          : currentTotal;
      },
      0,
    );

    if (getSelectedProductCategories().length === 2) {
      return totalDevicesGateway > 0 && totalDevicesRequest > 0;
    }

    return totalDevicesGateway > 0 || totalDevicesRequest > 0;
  }

  const filterTenderTypeByProductCategory = (productCategoryName) => {
    return Object.values(uniqueTerminals).filter(
      (terminal) =>
        terminal.product_category &&
        terminal.product_category === productCategoryName &&
        !terminal.is_product_category,
    );
  };

  const getDeviceFee = (terminalId) => {
    const deviceFee = terminalFees?.TerminalFeesData?.find(
      (fee) => fee.deviceModelName === uniqueTerminals[terminalId].name,
    );
    return deviceFee ? deviceFee.fixedFee : null;
  };

  const onDeviceQuantityChange = (terminalId, numberOfDevices) => {
    const deviceNewQuantity = parseInt(numberOfDevices);
    const deviceExistingQuantity = getDeviceOrderQuantity(terminalId);
    const deviceDetails = uniqueTerminals[terminalId];
    const deviceFee =
      deviceDetails.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline
        ? deviceDetails.retail_fee_vat_excl
        : getDeviceFee(terminalId);

    setDeviceOrder(terminalId, deviceNewQuantity, deviceFee);

    if (deviceExistingQuantity !== deviceNewQuantity) {
      TealiumEvents.paymentTypeSelectOption(customerType);
    }
  };

  const getProductNameByCategoryName = (productCategoryName) => {
    return Object.values(uniqueTerminals).find(
      (terminal) =>
        terminal.product_category &&
        terminal.product_category === productCategoryName &&
        terminal.is_product_category,
    ).name;
  };

  function onQuantityChange(values, isManualEdit) {
    Object.keys(values).forEach((key) => {
      const deviceId = quantityFieldNameToDeviceId(key);
      const deviceQuantity = values[key];
      onDeviceQuantityChange(deviceId, deviceQuantity, isManualEdit);
    });

    return {};
  }

  function getTerminalIDFromDeviceName(deviceName) {
    return Object.keys(uniqueTerminals)
      .filter((key) => uniqueTerminals[key].name === deviceName)
      .reduce((accum, terminal) => {
        accum[deviceName] = terminal;

        return accum;
      }, {});
  }

  function setDefaultTenderType(tenderType) {
    const tenderTypes = getDeviceQuantities();
    Object.keys(tenderTypes).forEach(
      (key) =>
        (tenderTypes[key] = Number(
          key === deviceIdToQuantityFieldName(getTerminalIDFromDeviceName(tenderType)),
        )),
    );

    onQuantityChange(tenderTypes, true);
    return tenderTypes;
  }

  const tenderTypeCard = (terminal, payment, terminalQuantities) => {
    return (
      <>
        <img className="logo" src={PRODUCT_IMAGE_MAP[terminal.name]} key={payment.id} />
        <Card
          className={
            !!terminalQuantities[deviceIdToQuantityFieldName(terminal.id)] ? 'greyed-out' : ''
          }
        >
          <div className={`top-card ${getSelectedProductCategories().length > 1 && 'small'}`}>
            <p className="title">{payment.title}</p>
            <p className="subtitle">{payment.subtitle}</p>
            <div
              className={`logo-box is-vcentered ${
                getSelectedProductCategories().length > 1 && 'small'
              }`}
            >
              {payment.subIcons.map((icon, index) => (
                <img
                  src={icon}
                  key={`${payment.id}+${index}`}
                  className={index % 2 === 0 ? 'has-padding-right-6' : ''}
                />
              ))}
            </div>
          </div>
          <div className="bottom-card">
            <div className="select-section">
              <div className={'card-padding'}>
                <Button
                  automationid={`Onboarding_SpinEdit_Quantity_${terminal.id}`}
                  buttonStyle={DefaultButtonStyle().hasTertiaryAction().build()}
                  buttonParams={{
                    type: ButtonParamType.NormalButton,
                    onClick: () => {
                      const updatedValue = Number(
                        !terminalQuantities[deviceIdToQuantityFieldName(terminal.id)],
                      );

                      onQuantityChange(
                        {
                          ...terminalQuantities,
                          [deviceIdToQuantityFieldName(terminal.id)]: updatedValue,
                        },
                        false,
                      );
                    },
                  }}
                  className="device-quantity-input"
                >
                  {!terminalQuantities[deviceIdToQuantityFieldName(terminal.id)]
                    ? 'Select'
                    : 'Unselect'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </>
    );
  };

  return (
    <div className="container PaymentType">
      <Formik initialValues={initialValues} enableReinitialize={true}>
        {({ values: terminalQuantities }) =>
          getSelectedProductCategories().map((productCategoryName, index) => (
            <React.Fragment key={`${productCategoryName}-${index}`}>
              <h3 className="product-category-name">
                Select your payment method for {getProductNameByCategoryName(productCategoryName)}
              </h3>
              <div className="card-container columns is-mobile has-text-centered">
                {filterTenderTypeByProductCategory(productCategoryName).map((terminal) => {
                  const payment = PAYMENT_LIST[terminal.name];
                  return (
                    <div className="column" key={payment.id}>
                      {tenderTypeCard(terminal, payment, terminalQuantities)}
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          ))
        }
      </Formik>
    </div>
  );
}
